/**
 * Responsibility: advance the aircraft's state by one fixed simulation step.
 *
 * A pure function of its arguments. No Three.js, no DOM, no clock of its own, no reading
 * of live input — everything it needs arrives as a parameter, which is what makes the feel
 * of the aircraft something that can be measured in a test rather than only flown.
 *
 * ## The model
 *
 * Turning comes from bank, not from a rudder. Control input sets a *target* roll; actual
 * roll eases toward it; heading rate is proportional to actual roll. That chain is the
 * whole reason a turn feels like a slow lean into a curve rather than a pivot: pressing
 * left does not turn the aircraft, it starts a bank, and the bank turns the aircraft.
 *
 * Pitch works the same way, with its own heavier time constant.
 *
 * ## No crashing, no ceiling bump
 *
 * The requirement is that the player cannot crash and cannot leave the world, without ever
 * hitting something that feels like a wall. Two mechanisms, in that order:
 *
 *   1. **Progressive limiting.** Within `FLARE_BAND` of the ground the permitted nose-down
 *      angle scales to zero, so holding a dive flares out into a level skim. The ceiling
 *      does the mirror image with climb.
 *   2. **A hard clamp**, as a backstop only. Progressive limiting cannot handle terrain
 *      rising underneath faster than the aircraft is climbing, so altitude is clamped after
 *      integration. Because terrain is continuous and a step covers only a few world units,
 *      the correction per step is small enough not to read as a snap.
 *
 * Doing it with the clamp alone would stop the aircraft dead with its nose still pointed
 * at the ground, which is exactly the kind of snap this project does not permit.
 */

import { damp } from '../core/damp.js';
import { FLIGHT, degToRad } from '../config.js';

/** Clamp a value into a range. */
function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

/** Clamp a control axis to the -1..1 the flight model is specified against. */
function clampAxis(value) {
  return Number.isFinite(value) ? clamp(value, -1, 1) : 0;
}

/** Steps per full sweep of the trim range, for reporting trim as a readable position. */
function trimStepCount(tuning) {
  return Math.max(1, Math.round((tuning.SPEED_TRIM_MAX - tuning.SPEED_TRIM_MIN) / tuning.SPEED_TRIM_STEP));
}

/**
 * The trim multiplier at a step position. The inverse of what `describeTrim` reports.
 *
 * @param {object} tuning
 * @param {number} step
 * @returns {number} Unclamped; callers clamp.
 */
function trimForStep(tuning, step) {
  const steps = trimStepCount(tuning);
  const fraction = Number.isFinite(step) ? step / steps : 0;
  return tuning.SPEED_TRIM_MIN + fraction * (tuning.SPEED_TRIM_MAX - tuning.SPEED_TRIM_MIN);
}

/**
 * Create a flight model bound to a set of tuning values.
 *
 * @param {object} [tuning] - Defaults to the `FLIGHT` block in `config.js`. Tests pass
 *   round numbers so that what is being measured is the model rather than the tuning.
 * @returns {{createInitialState: Function, step: Function, getTuning: () => object}}
 */
export function createFlightModel(tuning = FLIGHT) {
  const required = [
    'CRUISE_SPEED',
    'MAX_ROLL',
    'ROLL_TIME_CONSTANT',
    'TURN_RATE_AT_MAX_ROLL',
    'MAX_PITCH',
    'PITCH_TIME_CONSTANT',
    'MIN_GROUND_CLEARANCE',
    'FLARE_BAND',
    'CEILING',
    'CEILING_BAND',
    'SPEED_TRIM_MIN',
    'SPEED_TRIM_MAX',
    'SPEED_TRIM_STEP',
    'SPEED_TIME_CONSTANT',
  ];
  for (const key of required) {
    if (!Number.isFinite(tuning[key])) {
      throw new Error(`createFlightModel: tuning.${key} must be a finite number`);
    }
  }
  if (!(tuning.MAX_ROLL > 0) || !(tuning.MAX_PITCH > 0)) {
    throw new Error('createFlightModel: MAX_ROLL and MAX_PITCH must be positive');
  }
  if (!(tuning.ROLL_TIME_CONSTANT > 0) || !(tuning.PITCH_TIME_CONSTANT > 0)) {
    throw new Error('createFlightModel: time constants must be positive');
  }
  if (!(tuning.FLARE_BAND > 0) || !(tuning.CEILING_BAND > 0)) {
    // A zero band would collapse progressive limiting back into a bare clamp.
    throw new Error('createFlightModel: FLARE_BAND and CEILING_BAND must be positive');
  }
  if (!(tuning.SPEED_TRIM_MIN > 0)) {
    /*
      The requirements are explicit that there is never a full stop. A zero or negative minimum would
      allow hovering, or flying backwards, and turn a flight into a photograph.
    */
    throw new Error('createFlightModel: SPEED_TRIM_MIN must be greater than zero');
  }
  if (!(tuning.SPEED_TRIM_MAX >= tuning.SPEED_TRIM_MIN)) {
    throw new Error('createFlightModel: SPEED_TRIM_MAX must not be below SPEED_TRIM_MIN');
  }
  if (!(tuning.SPEED_TIME_CONSTANT > 0)) {
    throw new Error('createFlightModel: SPEED_TIME_CONSTANT must be positive');
  }

  // Converted once. Degrees are for humans reading config; the maths is in radians.
  const maxRoll = degToRad(tuning.MAX_ROLL);
  const maxPitch = degToRad(tuning.MAX_PITCH);
  const turnRateAtMaxRoll = degToRad(tuning.TURN_RATE_AT_MAX_ROLL);

  /** Hold a trim multiplier inside its configured range, treating nonsense as neutral. */
  function clampTrim(value) {
    if (!Number.isFinite(value)) return clamp(1, tuning.SPEED_TRIM_MIN, tuning.SPEED_TRIM_MAX);
    return clamp(value, tuning.SPEED_TRIM_MIN, tuning.SPEED_TRIM_MAX);
  }

  return {
    /**
     * Build a starting state.
     *
     * @param {object} [options]
     * @param {number} [options.x]
     * @param {number} [options.z]
     * @param {number} [options.y] - Defaults to `tuning.START_ALTITUDE`.
     * @param {number} [options.heading] - Radians. 0 faces negative Z.
     * @param {(x: number, z: number) => number} [options.sampleGround] - When supplied, the
     *   start altitude is lifted clear of the terrain, so spawning inside a mountain is
     *   impossible regardless of what the configured start altitude is.
     * @returns {{x: number, y: number, z: number, heading: number, roll: number, pitch: number}}
     */
    createInitialState({
      x = 0,
      z = 0,
      y = tuning.START_ALTITUDE,
      heading = 0,
      speedTrim = 1,
      sampleGround,
    } = {}) {
      let altitude = y;

      if (typeof sampleGround === 'function') {
        altitude = Math.max(altitude, sampleGround(x, z) + tuning.MIN_GROUND_CLEARANCE);
      }

      const trim = clampTrim(speedTrim);

      return {
        x,
        z,
        y: Math.min(altitude, tuning.CEILING),
        heading,
        roll: 0,
        pitch: 0,

        /*
          Roll and pitch always start at zero, even when resuming a save. Coming back mid-bank would
          mean the world tilts before you have touched anything.
        */

        /** Target speed multiplier the player has trimmed to. Persisted. */
        speedTrim: trim,

        /*
          Actual speed starts already at the trimmed target rather than easing up to it. On a fresh
          world there is nothing to ease from, and on a resumed one the save recorded a speed the
          player had settled into.
        */
        speed: tuning.CRUISE_SPEED * trim,
      };
    },

    /**
     * Advance one step.
     *
     * @param {{x: number, y: number, z: number, heading: number, roll: number, pitch: number}} state
     * @param {{roll: number, pitch: number}} controls - Axes in -1..1.
     * @param {number} dt - Fixed timestep in seconds.
     * @param {(x: number, z: number) => number} sampleGround - Ground elevation. Injected
     *   rather than imported so this module stays independent of world generation and can
     *   be tested against a synthetic ridge.
     * @returns {{x: number, y: number, z: number, heading: number, roll: number, pitch: number}}
     *   A new object; the input state is never modified.
     */
    step(state, controls, dt, sampleGround) {
      const rollAxis = clampAxis(controls?.roll);
      const pitchAxis = clampAxis(controls?.pitch);

      /*
        Roll first, because heading depends on it. Easing toward a capped target means the
        cap can never be exceeded — there is no separate clamp to get wrong, and releasing
        the key eases back to level for free because the target becomes zero.
      */
      const roll = damp(state.roll, rollAxis * maxRoll, dt, tuning.ROLL_TIME_CONSTANT);

      // A banked aircraft turns. Level flight cannot turn at all, which is the point.
      const heading = state.heading + (roll / maxRoll) * turnRateAtMaxRoll * dt;

      /*
        Progressive limiting. Descent is scaled down as the ground approaches and climb is
        scaled down as the ceiling approaches, each over its own band.
      */
      const clearance = state.y - sampleGround(state.x, state.z);
      const descentAllowance = clamp(
        (clearance - tuning.MIN_GROUND_CLEARANCE) / tuning.FLARE_BAND,
        0,
        1,
      );
      const climbAllowance = clamp((tuning.CEILING - state.y) / tuning.CEILING_BAND, 0, 1);

      let targetPitch = pitchAxis * maxPitch;
      if (targetPitch < 0) targetPitch *= descentAllowance;
      if (targetPitch > 0) targetPitch *= climbAllowance;

      const pitch = damp(state.pitch, targetPitch, dt, tuning.PITCH_TIME_CONSTANT);

      /*
        Speed eases toward whatever the player has trimmed to. Damped like every other axis, and more
        heavily: speed is the slowest thing an aircraft changes, and a snap between speeds would be
        the most jarring possible violation of "nothing responds instantly".
      */
      const speedTrim = clampTrim(state.speedTrim);
      const speed = damp(
        Number.isFinite(state.speed) ? state.speed : tuning.CRUISE_SPEED * speedTrim,
        tuning.CRUISE_SPEED * speedTrim,
        dt,
        tuning.SPEED_TIME_CONSTANT,
      );

      /*
        Integrate. Speed is along the flight path, so pitching trades horizontal progress
        for vertical: climbing steeply covers less ground, which is both correct and part of
        why pitching up feels like it has weight.
      */
      const horizontalSpeed = Math.cos(pitch) * speed;
      const x = state.x + Math.sin(heading) * horizontalSpeed * dt;
      const z = state.z - Math.cos(heading) * horizontalSpeed * dt;
      let y = state.y + Math.sin(pitch) * speed * dt;

      // Backstops. Normally inactive; see the file header.
      const floor = sampleGround(x, z) + tuning.MIN_GROUND_CLEARANCE;
      y = clamp(y, floor, Math.max(floor, tuning.CEILING));

      return { x, y, z, heading, roll, pitch, speedTrim, speed };
    },

    /**
     * A state with its speed trim stepped up or down, clamped to the configured range.
     *
     * Returns a new state rather than mutating, so trimming stays as pure as stepping does. Only the
     * *target* moves; actual speed eases toward it over the next few seconds.
     *
     * @param {object} state
     * @param {number} direction - Positive to speed up, negative to slow down.
     * @returns {object}
     */
    withTrimStep(state, direction) {
      const stepped = clampTrim(state.speedTrim) + Math.sign(direction) * tuning.SPEED_TRIM_STEP;
      return { ...state, speedTrim: clampTrim(stepped) };
    },

    /**
     * A state with its speed trim set to a given step position.
     *
     * For the menu's cruise-speed slider, which works in the same step units the readout displays. Going
     * through steps rather than raw multipliers means the slider and the keys cannot land on values the
     * other cannot reach.
     *
     * @param {object} state
     * @param {number} step - 0 is the slowest trim, `describeTrim().steps` the fastest.
     * @returns {object}
     */
    withTrimAtStep(state, step) {
      return { ...state, speedTrim: clampTrim(trimForStep(tuning, step)) };
    },

    /**
     * Where the trim sits, for display.
     *
     * Reported as a step index rather than a raw multiplier, because "4 of 7" is legible at a glance
     * and "1.15" is not.
     *
     * @param {object} state
     * @returns {{step: number, steps: number, multiplier: number, targetSpeed: number}}
     */
    describeTrim(state) {
      const steps = trimStepCount(tuning);
      const trim = clampTrim(state.speedTrim);
      const fraction = (trim - tuning.SPEED_TRIM_MIN) / (tuning.SPEED_TRIM_MAX - tuning.SPEED_TRIM_MIN || 1);

      return {
        step: Math.round(fraction * steps),
        steps,
        multiplier: trim,

        /*
          The speed this trim is heading for, as opposed to `state.speed` which is still easing toward
          it. Reported here so the UI can tell the player what the control they just pressed is *for* —
          the number it moves — without duplicating the multiplication.
        */
        targetSpeed: tuning.CRUISE_SPEED * trim,
      };
    },

    /**
     * The aircraft's velocity in world units per second.
     *
     * Derived rather than stored, because it is fully determined by heading, pitch, and the
     * cruise speed — keeping a copy in the state would be a second thing to keep in sync.
     * The chase camera needs it to lead its aim point, and this is the authoritative answer
     * to how fast the aircraft is going.
     *
     * @param {{heading: number, pitch: number}} state
     * @returns {{x: number, y: number, z: number}}
     */
    velocityFor(state) {
      /*
        Uses the state's actual speed, not the base cruise speed. The chase camera leads its aim by
        this vector, so an outdated speed here would leave the aircraft sitting off-centre whenever the
        player had trimmed away from neutral.
      */
      const speed = Number.isFinite(state.speed)
        ? state.speed
        : tuning.CRUISE_SPEED * clampTrim(state.speedTrim);
      const horizontal = Math.cos(state.pitch) * speed;

      return {
        x: Math.sin(state.heading) * horizontal,
        y: Math.sin(state.pitch) * speed,
        z: -Math.cos(state.heading) * horizontal,
      };
    },

    /** The tuning values in use. Exposed for debug readouts and tests. */
    getTuning() {
      return tuning;
    },
  };
}

/**
 * Blend two flight states for rendering between simulation steps.
 *
 * The simulation runs at a fixed 60 Hz while the display may not. Without this, a 144 Hz
 * monitor shows the same position for two or three frames and then jumps, which reads as
 * stutter — precisely the thing a fixed timestep is supposed to buy smoothness for.
 *
 * Heading is deliberately never wrapped into 0..2π anywhere in the model, which is what
 * lets it be blended with a plain lerp. Wrapping would put a discontinuity at the seam and
 * a sustained turn would flick the aircraft all the way around once per lap.
 *
 * @param {object} previous - State before the last step.
 * @param {object} current - State after the last step.
 * @param {number} alpha - Fraction of a step elapsed, 0..1.
 * @returns {object} A blended state. Not a valid input to `step`, only to rendering.
 */
export function blendFlightState(previous, current, alpha) {
  const t = clamp(Number.isFinite(alpha) ? alpha : 0, 0, 1);

  return {
    x: previous.x + (current.x - previous.x) * t,
    y: previous.y + (current.y - previous.y) * t,
    z: previous.z + (current.z - previous.z) * t,
    heading: previous.heading + (current.heading - previous.heading) * t,
    roll: previous.roll + (current.roll - previous.roll) * t,
    pitch: previous.pitch + (current.pitch - previous.pitch) * t,

    /*
      Speed is carried through because the chase camera derives its aim lead from it. A blended state
      missing it would make the camera compute the wrong velocity and leave the aircraft off-centre.
      Trim is taken from the current step rather than interpolated: it is a setting, not a motion.
    */
    speed: previous.speed + (current.speed - previous.speed) * t,
    speedTrim: current.speedTrim,
  };
}
